import React, { useContext } from 'react';
import {
  REBRAND_COLOR,
  PADDING,
  BREAKPOINT,
  COLOR,
  MARGIN,
  POSITION
} from '@latitude/core/utils/constants';
import { Heading4 } from '@latitude/heading';
import { Box } from '@latitude/box';
import { Metadata } from '@latitude/metadata';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import { StickyCta } from '@latitude/sticky-cta';
import StickyNavigation from '@/components/StickyNavigation/StickyNavigationV2';
import Layout from '../../components/layout';
import FeaturesSlider from '@/components/FeaturesSlider/FeaturesSlideV2';
import MobileAppInstallPrompts from '@latitude/mobile-app-install-prompt/MobileAppInstallPrompt';
import PageData from '../../data/pages/calculators-and-tools/personal-loan-repayment-calculator.json';
import footerData from '../../data/json/footer.json';
import withStickyState from '../../hoc/withStickyState';
import { SOFT_QUOTE_LOANS_URL } from '../../utils/softQuoteUtil';
import { PageContext } from '../../context/PageContext';
import { featuresSliderIE11Fix } from '../../utils/stylesUtils';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import Section from '@latitude/section-rebrand';
import ProductComparisonSection from '@/components/ProductComparisonSection/ProductComparisonSectionV2';
import CardCollection from '@/components/lab-components/CardCollection/CardCollectionV2';
import ProofPointCollection from '@/components/lab-components/ProofPointCollection/ProofPointCollectionV2';
import PromoBanner from '@/components/lab-components/PromoBanner/PromoBannerV2';
import Faq from '@/components/Faq/FaqV2/Faq';
import PersonalLoanCalculator from '@/components/PersonalLoanCalculatorRebrand/PersonalLoanCalculator';
import Button from '@latitude/button-rebrand';
import Typography from '@latitude/typography';
import StandaloneText from '@/components/lab-components/StandaloneTextRebrand';
import useApplyUrl from '../../hooks/useApplyUrl';

const PLCalculatorPage = props => {
  const featureSliderItems = [
    {
      icon: 'icon-snap-fingers-branded',
      title: 'Un-complicated loans',
      text:
        'Get an indicative Quote in 2 minutes, Apply in under 7 minutes. Once approved and you accept your contract, funds in your account in less than 24 hours.'
    },
    {
      icon: 'icon-slinky',
      title: 'Flexible options',
      text:
        'At Gem, you can choose to apply for a Fixed or Variable loan, along with an option to set up monthly, fortnightly or weekly repayments.'
    },
    {
      icon: 'icon-extras-branded',
      title: 'Redraw extra funds',
      text:
        "Get flexible access to funds when you need them, when you're ahead on repayments with a Variable Rate Personal Loan."
    }
  ];

  /** Contentful Data */
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || {};
  const featureSliderData = state?.featureSliderData || [
    {
      heading: 'Why choose a Gem Loan?',
      featureCards: featureSliderItems
    }
  ];
  const COLOUR_PALE_BLUE = '#E5F6FF';
  const { applyUrl } = useApplyUrl();
  const getEstimateUrl = 'https://loans.portal.gemfinance.co.nz/s/quote';
  /** - END - */
  return (
    <Layout location={props.location} noMetaKeywords customFooter={footerData}>
      <main className="navigation-spacer personal-loan-calculator-page">
        <Box backgroundColor={COLOR.GREY6}>
          <MobileAppInstallPrompts />
          <Metadata
            title={PageData.title}
            description={PageData.description}
            canonical={PageData.path}
          />
          <HeroV2
            {...(state?.heroBannerData?.[0] ||
              PageData?.contentful?.heroBannerData?.[0])}
          />

          <StickyNavigation
            items={PageData?.contentful?.inPageNavData?.items}
            phoneNumber={PageData.content.phoneNumber}
            isSticky={props.isNavSticky}
            ctaHref={SOFT_QUOTE_LOANS_URL}
            ctaText="Get rate estimate"
            onStickyNavStateChange={props.handleStickyNavStateChange}
            offsetElem="[data-sticky-navigation-offset]"
            trackId="sticky-nav-get-my-rate"
            trackEventData={{
              category: 'cta',
              action: 'quote-link',
              label: 'Get rate estimate',
              location: 'Sticky Navigation'
            }}
            trackProductId={['PLNZLF-WEB']}
            {...state?.inPageNavData?.[0]}
          />

          <div
            css={`
              background-color: ${REBRAND_COLOR.BLACK_20};
              padding-top: 32px;
              padding-bottom: 32px;
              && .typography-base__sh1 {
                margin-bottom: 30px !important;
              }
            `}
          >
            <Section id="calculator">
              <Typography.SH1 color={REBRAND_COLOR.BLACK} semibold>
                Get an estimate of your repayments
              </Typography.SH1>
              <PersonalLoanCalculator
                applyUrl={getEstimateUrl}
                applyCTATrackProductId={['PLNZLF-WEB']}
              />
            </Section>
          </div>

          <FeaturesSlider
            key={
              (
                state?.featureSliderData?.[0] ||
                PageData?.contentful?.featureSliderData[0]
              ).heading
            }
            heading={
              (
                state?.featureSliderData?.[0] ||
                PageData?.contentful?.featureSliderData[0]
              ).heading
            }
            id="why-us"
            className="why-choose bg-f8f8f8"
            subheading={
              (
                state?.featureSliderData?.[0] ||
                PageData?.contentful?.featureSliderData[0]
              ).description
            }
            backgroundColor={
              state?.featureSliderData?.[0]?.backgroundColor?.value ||
              PageData?.contentful?.featureSliderData[0]?.backgroundColor?.value
            }
            data={
              (
                state?.featureSliderData?.[0] ||
                PageData?.contentful?.featureSliderData[0]
              ).featureCards
            }
          />

          <StandaloneText
            {...(state?.standaloneTextData?.[0] ||
              PageData?.contentful?.standAloneTextData?.[0])}
          />

          <CardCollection
            id="applying-is-simple"
            data={
              state?.cardCollectionData?.[0] ||
              PageData?.contentful?.cardCollectionData?.[0]
            }
          />

          <ProofPointCollection
            id="calculators-and-tools"
            data={
              state?.proofPointCollectionData?.[0] ||
              PageData?.contentful?.proofPointCollectionData?.[0]
            }
          />

          <PromoBanner
            {...(state?.promoBannerData?.[0] ||
              PageData?.contentful?.promoBannerData?.[0])}
          />

          <ImportantInformation
            data={require('../../data/pages/calculators-and-tools/personal-loan-repayment-calculator.json')}
            css={`
              background-color: ${REBRAND_COLOR.STONE};
              overflow-x: hidden;
              .row {
                @media (min-width: 1600px) {
                  padding-left: ${PADDING.P32};
                  padding-right: ${PADDING.P32};
                }
              }
            `}
            {...{
              ...state?.importantInfoData?.[0],
              sectionOneColummClasses: 'col-12 col-lg-6',
              sectionTwoColummClasses: 'col-12 col-lg-6'
            }}
          />
        </Box>
      </main>
    </Layout>
  );
};

export default withStickyState(PLCalculatorPage);
